import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import { Modal, Spinner } from 'react-bootstrap';
import api from '../../services/api';

import ViewCILModal from './ViewCILModal'; // View modal

const CustomCILsTable = () => {
    // State for table data and filtering
    const [cils, setCILs] = useState([]);
    const [filterText, setFilterText] = useState('');

    // Modal states
    const [showViewModal, setShowViewModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);

    // Current selected CIL for view, assign or reject
    const [currentCIL, setCurrentCIL] = useState(null);
    const [selectedCIL, setSelectedCIL] = useState(null);

    // Reject modal state
    const [rejectReason, setRejectReason] = useState('');

    // Assign modal state: developers and projects selection
    const [developers, setDevelopers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [selectedDeveloper, setSelectedDeveloper] = useState('');
    const [selectedProject, setSelectedProject] = useState('');
    const [loading, setLoading] = useState(false);

    // Fetch only custom CILs (API: /cil/unpaginated?isCustom=true)
    const fetchCILs = async () => {
        setLoading(true);
        try {
            const res = await api.get('/cil/unpaginated?isCustom=1');
            if (res.data.response) {
                setCILs(res.data.response);
            }
        } catch (error) {
            console.error('Error fetching custom CILs:', error);
            toast.error('Failed to load custom CILs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCILs();
    }, []);

    // ---------- View Modal Handlers ----------
    const handleView = (cil) => {
        setCurrentCIL(cil);
        setShowViewModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setCurrentCIL(null);
        fetchCILs();
    };

    // ---------- Reject Modal Handlers ----------
    const openRejectModal = (cil) => {
        setSelectedCIL(cil);
        setShowRejectModal(true);
    };

    const handleCloseRejectModal = () => {
        setShowRejectModal(false);
        setRejectReason('');
        setSelectedCIL(null);
        fetchCILs();
    };

    const handleRejectConfirm = async () => {
        if (!rejectReason.trim()) {
            toast.error('Please provide a reason for rejection.');
            return;
        }
        setLoading(true);
        try {
            await api.post(`/cil/${selectedCIL.id}/reject`, { reason: rejectReason });
            toast.success('CIL rejected successfully!');
            handleCloseRejectModal();
        } catch (error) {
            console.error('Error rejecting CIL:', error);
            toast.error('Error rejecting CIL.');
        }
        setLoading(false);
    };

    // ---------- Assign Modal Handlers ----------
    const openAssignModal = async (cil) => {
        setSelectedCIL(cil);
        setShowAssignModal(true);
        // Fetch the approved developers from the API
        try {
            const endpoint = '/developers/approvedOnly';
            const { data } = await api.get(endpoint);
            if (data.status) {
                setDevelopers(data.response.data);
            } else {
                console.error('Error fetching developers:', data.message);
            }
        } catch (error) {
            console.error('Error fetching developers:', error);
        }
    };

    const handleCloseAssignModal = () => {
        setShowAssignModal(false);
        setSelectedCIL(null);
        setSelectedDeveloper('');
        setSelectedProject('');
        setProjects([]);
        fetchCILs();
    };

    // When a developer is selected, fetch the projects for that developer
    const handleDeveloperChange = async (e) => {
        const devId = e.target.value;
        setSelectedDeveloper(devId);
        try {
            const endpoint = `/projects/unpaginated?developer_id=${devId}`;
            const { data } = await api.get(endpoint);
            if (data.status) {
                setProjects(data.response.data);
            } else {
                console.error('Error fetching projects:', data.message);
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
            setProjects([]);
        }
    };

    const handleAssignSubmit = async () => {
        if (!selectedProject) {
            toast.error('Please select a project.');
            return;
        }
        setLoading(true);
        try {
            await api.put(`/cil/${selectedCIL.id}/assign`, { project_id: selectedProject });
            toast.success('CIL assigned successfully!');
            handleCloseAssignModal();
        } catch (error) {
            console.error('Error assigning CIL:', error);
            toast.error('Error assigning CIL.');
        }
        setLoading(false);
    };

    // ---------- Define Table Columns ----------
    const columns = [
        {
            name: 'No.',
            selector: (row) => row.id,
            sortable: true,
            width: '70px',
        },
        {
            name: 'Client Name',
            selector: (row) => row.clientName || 'N/A',
            sortable: true,
        },
        {
            name: 'Client Number',
            selector: (row) => row.clientNumber || 'N/A',
            sortable: true,
        },
        {
            name: 'Custom Project',
            selector: (row) => row.projectName || 'N/A',
            sortable: true,
        },
        {
            name: 'Custom Developer',
            selector: (row) => row.developerName || 'N/A',
            sortable: true,
        },
        {
            name: 'Developer Sales Number',
            selector: (row) => row.developerSalesNumber || 'N/A',
            sortable: false,
        },
        {
            name: 'Status',
            cell: (row) => (
                <span
                    style={{ whiteSpace: 'nowrap' }}
                    className={`bg-${row.status === 'rejected' ? 'danger' : 'warning'}-focus text-${row.status === 'rejected' ? 'danger' : 'warning'}-main px-24 py-4 rounded-pill fw-medium text-sm`}
                >
                    {row.status === 'rejected' ? 'Rejected' : 'Pending'}
                </span>
            ),
            sortable: true,
            minWidth: '180px',
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="d-flex flex-wrap align-items-center">
                    <button
                        className="btn btn-primary btn-sm me-2"
                        onClick={() => handleView(row)}
                    >
                        View
                    </button>
                    <button
                        className="btn btn-warning btn-sm me-2"
                        onClick={() => openAssignModal(row)}
                    >
                        Assign
                    </button>
                    {row.status !== 'rejected' && (
                        <button
                            className="btn btn-danger btn-sm"
                            onClick={() => openRejectModal(row)}
                        >
                            Reject
                        </button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            minWidth: '260px',
        },
    ];

    // CSV export headers
    const csvHeaders = [
        { label: 'CIL No.', key: 'id' },
        { label: 'Client Name', key: 'clientName' },
        { label: 'Client Number', key: 'clientNumber' },
        { label: 'Custom Project', key: 'projectName' },
        { label: 'Custom Developer', key: 'developerName' },
        { label: 'Developer Sales Number', key: 'developerSalesNumber' },
        { label: 'Status', key: 'status' },
        { label: 'Created At', key: 'created_at' },
        { label: 'Updated At', key: 'updated_at' }
    ];

    // Filter the data based on text input
    const filteredData = cils.filter((cil) => {
        const search = filterText.toLowerCase();
        return (
            String(cil.id).toLowerCase().includes(search) ||
            (cil.clientName || '').toLowerCase().includes(search) ||
            (cil.clientNumber || '').toLowerCase().includes(search) ||
            (cil.projectName || '').toLowerCase().includes(search) ||
            (cil.developerName || '').toLowerCase().includes(search)
        );
    });

    // Custom styles for DataTable rows
    const customStyles = {
        rows: {
            style: {
                fontSize: '15px',
            },
        },
    };

    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="card-body">
                {/* Filters and CSV Export */}
                <div className="d-flex justify-content-start align-items-center mb-3 flex-wrap gap-2">
                    <input
                        type="text"
                        placeholder="Search by any field..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        className="form-control"
                        style={{ maxWidth: '300px' }}
                    />
                    <CSVLink
                        data={cils} // Export all custom CILs
                        headers={csvHeaders}
                        filename="custom-cils-data-all.csv"
                        className="btn btn-secondary"
                    >
                        Export All Custom CILs CSV
                    </CSVLink>
                </div>

                <DataTable
                    columns={columns}
                    data={filteredData}
                    progressPending={loading}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    customStyles={customStyles}
                    noDataComponent={loading ? "" : "No results found"}
                />
            </div>

            {/* --------------------- Modals --------------------- */}

            {/* View CIL Modal */}
            {showViewModal && (
                <ViewCILModal
                    show={showViewModal}
                    handleClose={handleCloseViewModal}
                    cil={currentCIL}
                />
            )}

            {/* Assign Modal */}
            <Modal show={showAssignModal} onHide={handleCloseAssignModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Assign Project to Custom CIL</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="developerSelect" className="form-label">
                            Select Developer
                        </label>
                        <select
                            id="developerSelect"
                            className="form-select"
                            value={selectedDeveloper}
                            onChange={handleDeveloperChange}
                        >
                            <option value="">-- Select Developer --</option>
                            {developers.map((dev) => (
                                <option key={dev.id} value={dev.id}>
                                    {dev.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {selectedDeveloper && (
                        <div className="mb-3">
                            <label htmlFor="projectSelect" className="form-label">
                                Select Project
                            </label>
                            <select
                                id="projectSelect"
                                className="form-select"
                                value={selectedProject}
                                onChange={(e) => setSelectedProject(e.target.value)}
                            >
                                <option value="">-- Select Project --</option>
                                {projects.map((proj) => (
                                    <option key={proj.id} value={proj.id}>
                                        {proj.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-success" onClick={handleAssignSubmit} disabled={loading}>
                        {loading ? <Spinner animation="border" size="sm" /> : 'Assign'}
                    </button>
                    <button className="btn btn-secondary" onClick={handleCloseAssignModal}>
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Reject Confirmation Modal */}
            <Modal show={showRejectModal} onHide={handleCloseRejectModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Reject CIL</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to reject this CIL?</p>
                    <div className="mb-3">
                        <label htmlFor="rejectReason" className="form-label">
                            Reason for Rejection
                        </label>
                        <textarea
                            id="rejectReason"
                            className="form-control"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={3}
                            required
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-danger" onClick={handleRejectConfirm} disabled={loading}>
                        {loading ? <Spinner animation="border" size="sm" /> : 'Yes, Reject'}
                    </button>
                    <button className="btn btn-secondary" onClick={handleCloseRejectModal}>
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CustomCILsTable;
